'use client';

import { useI18n } from '~/features/i18n';
import useNotifications from '~/features/notifications/useNotifications';
import { usePortalCtx } from '../global/portal';
import { useCartCtx } from './context';
import { useTRPC } from '~/modules/trpc/client';
import type { TRPC_ROUTER } from '~/modules/trpc/routers';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { usePostHog } from 'posthog-js/react';
import { mapUnion } from '@funkey/shared';
import { cartHovercardEvent } from './cartHovercardEvents';
import type { CartItemsGrouped } from './shared';

type CartQueryData = TRPC_ROUTER.Outputs['cart']['get'];

// NOTE: this should probably just be split up into separate hooks.
export default function useCartState() {
    const trpc = useTRPC();
    const posthog = usePostHog();

    const i18n = useI18n();
    const notificationCtx = useNotifications();
    const portalCtx = usePortalCtx();

    const cartCtx = useCartCtx();
    const queryClient = useQueryClient();

    return {
        ctx: cartCtx,

        cartQueryOptions() {
            return trpc.cart.get.queryOptions(void undefined);
        },

        cartItemsGrouped_queryOptions() {
            return trpc.cart.get.queryOptions(void null, {
                select(cart) {
                    if (!cart) return null;
                    return cart.items.reduce<CartItemsGrouped>(
                        (acc, next) => {
                            acc.all.push(next);

                            if (
                                next.activityId === null ||
                                (portalCtx.portal === 'BIZZ' &&
                                    portalCtx.bizz.excludedActivityIDs.includes(next.activityId))
                            ) {
                                acc.unavailable.push(next);
                            } else {
                                acc.available.push(next);
                            }

                            return acc;
                        },
                        {
                            all: [],
                            available: [],
                            unavailable: [],
                        } satisfies CartItemsGrouped,
                    );
                },
            });
        },

        useCartQuery() {
            return useQuery(trpc.cart.get.queryOptions(void undefined, {}));
        },

        useCartSuspenseQuery() {
            return useSuspenseQuery(trpc.cart.get.queryOptions(void undefined));
        },

        useUpsertCartItemMutation() {
            const options = trpc.cart.items.upsert.mutationOptions();

            return useMutation({
                mutationKey: options.mutationKey,
                async mutationFn(variables: TRPC_ROUTER.Inputs['cart']['items']['upsert']) {
                    // Prevent adding duplicate activity+variant (only for new items)
                    if (variables.cartItemID === null && cartCtx.id) {
                        const cachedCart = queryClient.getQueryData<CartQueryData>(
                            trpc.cart.get.queryKey(),
                        );
                        if (cachedCart?.items) {
                            const isDuplicate = cachedCart.items.some(
                                item => item.activityVariantId === variables.variantId,
                            );
                            if (isDuplicate) {
                                throw new Error(
                                    i18n._('cart.duplicate-variant', undefined, {
                                        message: 'This activity variant is already in your cart.',
                                    }),
                                );
                            }
                        }
                    }

                    if (!cartCtx.id) {
                        const newCart = await trpc.cart.create.mutationOptions().mutationFn!();
                        // Mirror the new cart id in browser state after the server created it.
                        cartCtx.syncID(newCart.id);
                    }

                    return options.mutationFn!(variables);
                },
                onSuccess: async (data, variables) => {
                    // The server starts a new cart when the cookie's one was converted; its
                    // Set-Cookie can't be relied on through the streamed batch response.
                    if (data.cartId !== cartCtx.id) cartCtx.syncID(data.cartId);

                    const isUpdate = variables.cartItemID !== null;
                    if (!isUpdate) {
                        posthog.capture('cart.item.added', {
                            scope: mapUnion(portalCtx.portal, {
                                BIZZ: 'BIZZ',
                                ADMIN: 'ADMIN',
                                PARTNER: 'PARTNER',
                                WEBSITE: 'WEB',
                            }),
                            activity_id: (await data.cartItem).activityId,
                            variant_id: variables.variantId,
                            language_id: variables.languageId,
                            cart_id: data.cartId,
                            boostAdId: variables.boostAdId,
                        });
                    }

                    await cartCtx.invalidateQueries();

                    if (!isUpdate) {
                        cartHovercardEvent.emit();
                    }
                },
                onError(error) {
                    cartCtx.invalidateQueries();
                    console.error('error', error);
                    notificationCtx.dispatchError({ message: error.message });
                },
            });
        },

        useDeleteCartItemMutation({
            optimistic = false,
            silentSuccess = false,
        }: {
            optimistic?: boolean;
            silentSuccess?: boolean;
        } = {}) {
            const options = trpc.cart.items.delete.mutationOptions();

            return useMutation({
                ...options,
                async onMutate(variables) {
                    const cartQueryKey = trpc.cart.get.queryKey();
                    const countQueryKey = trpc.cart.items.count.queryKey();

                    await Promise.all([
                        queryClient.cancelQueries({ queryKey: cartQueryKey }),
                        queryClient.cancelQueries({ queryKey: countQueryKey }),
                    ]);

                    const previousCart = queryClient.getQueryData<CartQueryData>(cartQueryKey);
                    const previousCount = queryClient.getQueryData(countQueryKey);

                    if (optimistic) {
                        queryClient.setQueryData<CartQueryData>(cartQueryKey, prev => {
                            if (!prev) return prev;

                            return {
                                ...prev,
                                items: prev.items.filter(item => Number(item.id) !== variables.id),
                            };
                        });

                        queryClient.setQueryData<number | undefined>(countQueryKey, prev =>
                            typeof prev === 'number' ? Math.max(prev - 1, 0) : prev,
                        );
                    }

                    return { previousCart, previousCount };
                },
                onSuccess(_data) {
                    cartCtx.invalidateQueries();
                    if (!silentSuccess) {
                        notificationCtx.dispatchSuccess({
                            message: i18n._('Successfully removed from cart.'),
                        });
                    }
                },
                onError(error, _variables, context) {
                    if (optimistic) {
                        queryClient.setQueryData(trpc.cart.get.queryKey(), context?.previousCart);
                        queryClient.setQueryData(
                            trpc.cart.items.count.queryKey(),
                            context?.previousCount,
                        );
                    }
                    cartCtx.invalidateQueries();
                    notificationCtx.dispatchError({ message: error.message });
                },
            });
        },

        /** Converts a cart and removes the cartID cookie */
        useConvertCartMutation() {
            return useMutation(
                trpc.cart.markAsConverted.mutationOptions({
                    onSuccess() {
                        cartCtx.syncID(null);
                        cartCtx.invalidateQueries();
                    },
                }),
            );
        },
    };
}
